/**
 * Damage Pie — signature visual. Conic-gradient wedges + outer hairline rules
 * + label ring. Center disc shows largest category and its rupee weight.
 *
 * Pure CSS rendering — no SVG, no canvas. Each wedge is a slice of the conic.
 * Labels are absolutely placed at the wedge midpoint angle.
 */
import { store } from '../store.ts';
import { formatINR, CATEGORY_DISPLAY } from '../types.ts';
import type { Category } from '../types.ts';

// palette ring — one entry per top-N category (in oklch)
const WEDGE_COLORS = [
  'var(--saffron)',
  'var(--gold)',
  'var(--rose)',
  'var(--leaf)',
  'var(--teal)',
  'var(--bronze)',
  'var(--plum)',
];

const TOP_N = 7;

export class HisaabDamagePie extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.totals.by_category as Record<Category, number>, s.summary.totals.inr);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(byCat: Record<Category, number>, _total: number): void {
    const entries = (Object.entries(byCat) as [Category, number][])
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_N);

    if (entries.length === 0) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }

    const sum = entries.reduce((acc, [, v]) => acc + v, 0);
    const slices = entries.map(([cat, v], i) => ({
      cat,
      v,
      pct: sum > 0 ? v / sum : 0,
      color: WEDGE_COLORS[i % WEDGE_COLORS.length] ?? 'var(--saffron)',
    }));

    // build conic-gradient stops
    let acc = 0;
    const stops: string[] = [];
    const rules: string[] = [];
    const labelPositions: { angle: number; mid: number; slice: typeof slices[number] }[] = [];
    slices.forEach((s) => {
      const from = acc * 360;
      const to = (acc + s.pct) * 360;
      stops.push(`${s.color} ${from}deg ${to}deg`);
      // hairline rule between wedges at start angle (skip 0deg)
      if (from > 0.5) {
        rules.push(`oklch(98% 0.01 80 / 0) ${from - 0.35}deg, oklch(98% 0.02 80 / 0.85) ${from - 0.05}deg ${from + 0.05}deg, oklch(98% 0.01 80 / 0) ${from + 0.35}deg`);
      }
      labelPositions.push({ angle: from, mid: (from + to) / 2, slice: s });
      acc += s.pct;
    });
    const rulesGradient = rules.length
      ? `conic-gradient(from 0deg, ${rules.join(', ')})`
      : 'transparent';

    // biggest wedge — for outer glow ring (slices is non-empty: entries.length > 0)
    const biggest = slices[0]!;
    const biggestFrom = 0;
    const biggestTo = biggest.pct * 360;

    // place labels — radius ~50% from center (just outside the pie edge)
    const labelRadius = 52; // percent from center
    const labels = labelPositions.map(({ mid, slice }) => {
      // convert mid angle (0 at top, clockwise) to radians; conic starts from 12 o'clock by default in CSS conic-gradient
      const rad = (mid - 90) * Math.PI / 180;
      const x = 50 + labelRadius * Math.cos(rad);
      const y = 50 + labelRadius * Math.sin(rad);
      // only show label if slice >= 6%
      if (slice.pct < 0.06) return '';
      const name = CATEGORY_DISPLAY[slice.cat] ?? slice.cat;
      const pct = (slice.pct * 100).toFixed(0);
      return /* html */ `
<div class="pie-label" style="left:${x.toFixed(2)}%;top:${y.toFixed(2)}%;">
  <b>${name}</b>
  <span class="pct">${pct}%</span>
</div>`;
    }).join('');

    const biggestName = CATEGORY_DISPLAY[biggest.cat] ?? biggest.cat;
    const biggestAmt = formatINR(biggest.v);

    const legend = slices.map((s) => /* html */ `
<div class="pie-legend-row" style="--swatch:${s.color};">
  <span class="pie-legend-swatch"></span>
  <span>${CATEGORY_DISPLAY[s.cat] ?? s.cat}</span>
  <span class="pct">${(s.pct * 100).toFixed(0)}%</span>
</div>`).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: block;
  view-transition-name: damage;
}
.damage {
  position: relative;
  isolation: isolate;
  background:
    radial-gradient(ellipse 80% 60% at 50% 50%, oklch(17% 0.06 280) 0%, var(--paper-card) 70%);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: 32px 24px 30px;
  overflow: hidden;
}
.eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 22px;
  text-align: center;
}
.pie-stage {
  position: relative;
  width: min(360px, 88%);
  aspect-ratio: 1;
  margin-inline: auto;
}
.pie {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: conic-gradient(from 0deg, ${stops.join(', ')});
  box-shadow:
    0 0 0 1px var(--rule-strong),
    0 24px 64px -28px oklch(2% 0.02 280 / 0.85),
    inset 0 0 60px oklch(8% 0.02 280 / 0.4);
  animation: pie-reveal 1100ms cubic-bezier(0.65, 0, 0.35, 1) both;
}
@keyframes pie-reveal {
  from { transform: rotate(-12deg) scale(0.92); opacity: 0; filter: blur(6px); }
  to   { transform: rotate(0)      scale(1);    opacity: 1; filter: blur(0); }
}
.pie::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: ${rulesGradient};
  pointer-events: none;
  mix-blend-mode: overlay;
  opacity: 0.55;
}
.pie::after {
  content: '';
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent ${Math.max(0, biggestFrom - 1)}deg,
    oklch(85% 0.18 70 / 0.7) ${biggestFrom + 0.3}deg,
    oklch(85% 0.18 70 / 0.7) ${biggestTo - 0.3}deg,
    transparent ${biggestTo + 1}deg,
    transparent 360deg);
  filter: blur(14px);
  opacity: 0.55;
  pointer-events: none;
  z-index: -1;
}
.pie-center {
  position: absolute;
  inset: 22%;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 30%, oklch(22% 0.06 280), oklch(13% 0.03 280) 75%);
  border: 1px solid var(--rule-strong);
  display: grid;
  place-items: center;
  text-align: center;
  padding: 14px;
  box-shadow:
    inset 0 0 24px oklch(8% 0.02 280 / 0.7),
    0 6px 18px -8px oklch(2% 0 0 / 0.6);
  z-index: 2;
}
.pie-center-inner { display: grid; gap: 6px; }
.pie-biggest-cap {
  font-family: var(--mono);
  font-size: 8px;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.pie-biggest-cat {
  font-family: var(--display);
  font-style: italic;
  font-weight: 600;
  color: var(--saffron-glow, var(--saffron));
  font-size: clamp(18px, 5.4cqw, 22px);
  letter-spacing: 0.005em;
  line-height: 1.05;
}
.pie-biggest-amt {
  font-family: var(--display);
  font-weight: 700;
  color: var(--ink);
  font-size: clamp(20px, 6.6cqw, 28px);
  letter-spacing: -0.015em;
  font-feature-settings: 'tnum' 1, 'lnum' 1, 'ss01' 1;
}
.pie-labels {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.pie-label {
  position: absolute;
  transform: translate(-50%, -50%);
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-dim);
  display: grid;
  gap: 2px;
  text-align: center;
  pointer-events: auto;
  white-space: nowrap;
  text-shadow: 0 1px 2px oklch(8% 0.02 280 / 0.85);
}
.pie-label b {
  font-family: var(--display);
  font-style: italic;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0;
  text-transform: none;
  color: var(--ink);
}
.pie-label .pct { color: var(--saffron); font-size: 8px; }

.pie-legend {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 22px;
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid var(--rule-hair);
}
.pie-legend-row {
  display: grid;
  grid-template-columns: 10px 1fr auto;
  gap: 10px;
  align-items: center;
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--ink-dim);
}
.pie-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: var(--swatch, var(--saffron));
  box-shadow: 0 0 0 1px oklch(0% 0 0 / 0.4) inset;
}
.pct {
  color: var(--ink);
  font-feature-settings: 'tnum' 1, 'lnum' 1;
}
@media (prefers-reduced-motion: reduce) {
  .pie { animation: none; }
}
</style>
<div class="damage">
  <div class="eyebrow">Kis cheez ne maara · top ${slices.length}</div>
  <div class="pie-stage">
    <div class="pie">
      <div class="pie-center">
        <div class="pie-center-inner">
          <div class="pie-biggest-cap">No. 1 chot</div>
          <div class="pie-biggest-cat">${biggestName}</div>
          <div class="pie-biggest-amt">${biggestAmt}</div>
        </div>
      </div>
    </div>
    <div class="pie-labels">${labels}</div>
  </div>
  <div class="pie-legend">${legend}</div>
</div>
`;
  }
}

customElements.define('hisaab-damage-pie', HisaabDamagePie);
